/**
 * Every string in the contact form, in both languages.
 *
 * ── Why this file exists ──────────────────────────────────────────────────────
 * The site has 22 Tamil pages and had no Tamil contact form. Every Tamil CTA —
 * the nav's தொடர்பு, the Tamil footer, "Demo பார்க்கணுமா?" on the five Tamil
 * city pages — pointed at the English `/contact`. So a builder who read five
 * pages in Tamil, decided, and clicked the Tamil button landed on *Full name ·
 * Company / firm · Sites you manage*. That is the moment the entire Tamil bet
 * is cashed in, and it was the one page of the journey nobody had translated.
 *
 * The form is one component with the copy injected, not two forms: a second
 * copy of the validation, the honeypot, the captcha fallback and the
 * attribution wiring would drift from this one within a release, and the half
 * that drifted would be the half nobody tests.
 *
 * ── The register ──────────────────────────────────────────────────────────────
 * Spoken Tamil Nadu Tamil with English for construction and software terms —
 * demo, site, estimate, WhatsApp, email. This is the rule the rest of the Tamil
 * tree already follows (src/lib/ta/*, src/lib/catalog/suites.ts) and it is not
 * laziness: a builder here says those words in English every day, and literary
 * Tamil for them reads as though written by someone who has never stood on a
 * site.
 *
 * TODO(owner): this Tamil copy has NOT been reviewed by a native speaker, same
 * as the rest of the Tamil tree. It is the single highest-stakes Tamil page on
 * the site — a form is where awkward phrasing costs a lead rather than a
 * reader — so it should go to the top of that review queue.
 */

export interface ContactCopy {
  /** Form card heading + the one instruction under it. */
  formTitle: string;
  formHint: string;
  labels: {
    name: string; email: string; company: string; phone: string;
    interest: string; sites: string; message: string;
  };
  interests: { value: string; label: string }[];
  sitesRanges: { value: string; label: string }[];
  submit: string;
  submitting: string;
  /**
   * `generic` carries a literal `{email}` placeholder rather than being a
   * function of the address. The copy crosses the RSC boundary — /contact and
   * /ta/contact are Server Components rendering a client form — and a function
   * cannot be serialised across it (the build fails outright). A string with
   * one token can.
   */
  errors: { shortPhone: string; generic: string };
  success: {
    title: string; body: string; bodySupport: string;
    /** `{asset}` is replaced with the asset's label. */
    bodyAsset: string;
    download: string;
  };
  /**
   * The compact form a guide embeds beside its sheet.
   *
   * A guide's reader came for a file, not to contact a software company, and
   * sending them to /contact asked them to work out for themselves that the
   * form IS the download. `steps` says it in three lines before they type
   * anything, which is the whole reason this variant exists.
   */
  download: {
    title: string;
    /** `{asset}` is replaced with the asset's label. */
    hint: string;
    steps: [string, string, string];
    submit: string;
    submitting: string;
  };
  /** Honeypot label — never shown to a sighted user, read by screen readers. */
  honeypotLabel: string;
}

export const CONTACT_COPY_EN: ContactCopy = {
  formTitle: 'Tell us about your business',
  formHint: "Takes under a minute. We'll come back within one business day.",
  labels: {
    name: 'Full name',
    email: 'Email',
    company: 'Company / firm',
    phone: 'Phone',
    interest: 'How can we help?',
    sites: 'Sites you manage',
    message: 'Anything else? (optional)',
  },
  interests: [
    { value: 'demo',        label: 'Book a demo' },
    { value: 'trial',       label: 'Request a trial' },
    { value: 'pricing',     label: 'Discuss pricing' },
    { value: 'partnership', label: 'Partnership / referral' },
    { value: 'support',     label: 'Existing customer — support' },
    { value: 'other',       label: 'Something else' },
  ],
  sitesRanges: [
    { value: '1-3',   label: '1–3 sites' },
    { value: '4-10',  label: '4–10 sites' },
    { value: '11-25', label: '11–25 sites' },
    { value: '26+',   label: '26+ sites' },
  ],
  submit: 'Send message',
  submitting: 'Sending…',
  errors: {
    shortPhone: 'That phone number looks too short — please check it.',
    generic: 'Something went wrong. Please try again, or email {email}.',
  },
  success: {
    title: 'We received it.',
    body: 'Our team will reach out within one business day to set up your demo. No software to install in the meantime.',
    bodySupport: 'Our team will come back to you within one business day. If it is urgent, reply to the confirmation email and it reaches the same people.',
    bodyAsset: 'Your {asset} is ready below, and a copy is on its way to your inbox. No sales call unless you ask for one.',
    download: 'Download the sheet',
  },
  download: {
    title: 'Where should we send it?',
    hint: 'Four details and the {asset} is yours. No cost, no account, no obligation.',
    steps: [
      'Fill the four boxes below',
      'The file downloads here, straight away',
      'We email you a copy as well, so you do not lose it',
    ],
    submit: 'Get the file',
    submitting: 'One moment…',
  },
  honeypotLabel: 'Reference code (leave this empty)',
};

export const CONTACT_COPY_TA: ContactCopy = {
  formTitle: 'உங்க business பற்றி சொல்லுங்க',
  formHint: 'ஒரு நிமிஷம் கூட ஆகாது. ஒரு வேலை நாளுக்குள்ள நாங்க கூப்பிடுவோம்.',
  labels: {
    name: 'பேரு',
    email: 'Email',
    company: 'Company / firm',
    phone: 'Phone number',
    interest: 'என்ன உதவி வேணும்?',
    sites: 'எத்தனை site பாக்குறீங்க?',
    message: 'வேற எதுனா சொல்லணுமா? (optional)',
  },
  interests: [
    { value: 'demo',        label: 'Demo பாக்கணும்' },
    { value: 'trial',       label: 'Trial வேணும்' },
    { value: 'pricing',     label: 'விலை பற்றி பேசணும்' },
    { value: 'partnership', label: 'Partnership / referral' },
    { value: 'support',     label: 'ஏற்கனவே customer — support வேணும்' },
    { value: 'other',       label: 'வேற ஏதோ' },
  ],
  sitesRanges: [
    { value: '1-3',   label: '1–3 site' },
    { value: '4-10',  label: '4–10 site' },
    { value: '11-25', label: '11–25 site' },
    { value: '26+',   label: '26+ site' },
  ],
  submit: 'அனுப்புங்க',
  submitting: 'அனுப்பிட்டு இருக்கோம்…',
  errors: {
    shortPhone: 'இந்த number சரியா இல்லை போல — ஒரு தடவை பாருங்க.',
    generic: 'ஏதோ தப்பா போயிடுச்சு. மறுபடி try பண்ணுங்க, இல்லைனா {email}-க்கு email பண்ணுங்க.',
  },
  success: {
    title: 'கிடைச்சிடுச்சு.',
    body: 'ஒரு வேலை நாளுக்குள்ள எங்க team உங்களை கூப்பிட்டு demo set பண்ணுவாங்க. அதுக்குள்ள எந்த software-ம் install பண்ண வேண்டாம்.',
    bodySupport: 'ஒரு வேலை நாளுக்குள்ள எங்க team உங்களை கூப்பிடுவாங்க. அவசரம்-ன்னா, confirmation email-க்கு reply பண்ணுங்க — அதே ஆட்களுக்கே போகும்.',
    bodyAsset: 'உங்க {asset} கீழே தயாரா இருக்கு, ஒரு copy email-லயும் வரும். நீங்க கேட்டாத்தான் sales call — இல்லைனா இல்ல.',
    download: 'Sheet-ஐ download பண்ணுங்க',
  },
  download: {
    title: 'எங்க அனுப்பணும்?',
    hint: 'நாலு விவரம் கொடுத்தா {asset} உங்களுது. காசு இல்ல, account இல்ல, கட்டாயம் இல்ல.',
    steps: [
      'கீழ இருக்க நாலு box-ஐ fill பண்ணுங்க',
      'File இங்கயே உடனே download ஆகும்',
      'ஒரு copy email-லயும் அனுப்புவோம், தொலைஞ்சு போகாம இருக்க',
    ],
    submit: 'File-ஐ வாங்குங்க',
    submitting: 'ஒரு நிமிஷம்…',
  },
  honeypotLabel: 'Reference code (இதை காலியா விடுங்க)',
};
